import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MRT_Row, MRT_TableOptions, MRT_SortingState } from 'material-react-table';
import { ContractRouteDto } from 'src/generated/services/TFinancialApi';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useUpsertContractRoute, useDeleteContractRoute } from 'src/use-cases/settings/useContracts';
import { openToast } from 'src/use-cases/toast/useToast';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';

interface RouteDetailsTableProps {
  contractId: string;
  routes: ContractRouteDto[];
}

export const RouteDetailsTable = forwardRef<any, RouteDetailsTableProps>(({ contractId, routes }, ref) => {
  const [rowToDelete, setRowToDelete] = useState<ContractRouteDto | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isMutating, setIsMutating] = useState(false);

  // Default sort by creation date descending
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'createdOn',
      desc: true,
    },
  ]);

  const { mutateAsync: deleteRoute, isLoading: isDeleting } = useDeleteContractRoute();
  const { mutateAsync: upsertRoute } = useUpsertContractRoute();

  // Helper function to clear validation errors
  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);

  // Validate route fields
  const onValidateRoute = useCallback((route: Record<string, any>) => {
    const validationResult = {
      route: !route.route || route.route.trim() === '' ? 'Route is required' : '',
      polZoneCode: !route.polZoneCode || route.polZoneCode.trim() === '' ? 'POL Zone Code is required' : '',
      podZoneCode: !route.podZoneCode || route.podZoneCode.trim() === '' ? 'POD Zone Code is required' : '',
    };
    return validationResult;
  }, []);

  // CREATE action
  const handleCreateRoute = async ({ values, exitCreatingMode }: Parameters<NonNullable<MRT_TableOptions<ContractRouteDto>['onCreatingRowSave']>>[0]) => {
    const newValidationErrors = onValidateRoute(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      await upsertRoute({
        contractId,
        route: values.route,
        polZoneCode: values.polZoneCode,
        podZoneCode: values.podZoneCode,
      });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Route created successfully!',
      });
      exitCreatingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };

  // UPDATE action
  const handleUpdateRoute = async ({ row, values, exitEditingMode }: Parameters<NonNullable<MRT_TableOptions<ContractRouteDto>['onEditingRowSave']>>[0]) => {
    const newValidationErrors = onValidateRoute(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      await upsertRoute({
        id: row.original.id,
        contractId,
        route: values.route,
        polZoneCode: values.polZoneCode,
        podZoneCode: values.podZoneCode,
      });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Route updated successfully!',
      });
      exitEditingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };

  // DELETE action
  const handleDeleteRoute = async (route: ContractRouteDto) => {
    try {
      await deleteRoute({ contractRouteId: route.id!, contractId });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Route deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Render row actions
  const renderRowActions = useCallback(
    ({ row, table }: { row: MRT_Row<ContractRouteDto>; table: any }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  // Define columns for the Route table
  const routeColumns = useMemo<MRT_ColumnDef<ContractRouteDto>[]>(
    () => [
      {
        accessorKey: 'route',
        header: 'Route',
        size: 200,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.route,
          helperText: validationErrors?.route,
          onFocus: () => clearValidationError('route'),
          placeholder: 'e.g., SEA-FCL, AIR-FCL',
        },
      },
      {
        accessorKey: 'polZoneCode',
        header: 'POL Zone Code',
        size: 150,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.polZoneCode,
          helperText: validationErrors?.polZoneCode,
          onFocus: () => clearValidationError('polZoneCode'),
        },
      },
      {
        accessorKey: 'podZoneCode',
        header: 'POD Zone Code',
        size: 150,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.podZoneCode,
          helperText: validationErrors?.podZoneCode,
          onFocus: () => clearValidationError('podZoneCode'),
        },
      },
      {
        enableEditing: false,
        accessorKey: 'createdOn',
        header: 'Creation Date',
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return value ? format(new Date(value), 'd-MMM-yyyy') : '-';
        },
      },
    ],
    [validationErrors, clearValidationError]
  );

  // Configure the route table
  const routeTable = useMaterialReactTable({
    columns: routeColumns,
    data: routes || [],
    enableSorting: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableEditing: true,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    positionActionsColumn: 'first',
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: (params) => void handleCreateRoute(params),
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: (params) => void handleUpdateRoute(params),
    state: {
      sorting,
      isLoading: isMutating,
    },
    onSortingChange: setSorting,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 120,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4 text-14'>No routes found</p>,
    renderRowActions,
  });

  // Expose table methods to parent component
  useImperativeHandle(ref, () => ({
    openCreatingRow: () => routeTable.setCreatingRow(true),
    closeCreatingRow: () => routeTable.setCreatingRow(null),
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={routeTable} />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteRoute(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this route?</p>
      </ModalConfirm>
    </div>
  );
});

// Set display name for devtools
RouteDetailsTable.displayName = 'RouteDetailsTable';
